import { scopeProjectRef } from "@praxa/client-runtime/environment";
import { FolderIcon } from "lucide-react";
import * as Schema from "effect/Schema";
import { useEffect, useMemo, useState, type KeyboardEvent } from "react";
import { useParams } from "@tanstack/react-router";

import { useComposerDraftStore } from "../composerDraftStore";
import { getLocalStorageItem, setLocalStorageItem } from "../hooks/useLocalStorage";
import { useResizableWidth } from "../hooks/useResizableWidth";
import { resolveThreadRouteTarget } from "../threadRoutes";
import { useProject, useProjects, useThread, useThreadShells } from "../state/entities";
import { useClientSettings } from "../hooks/useSettings";
import { useSidebarVisibility } from "./ui/sidebar";
import { sortScopedProjectsForSidebar } from "./Sidebar.logic";
import FileBrowserPanel from "./files/FileBrowserPanel";
import {
  Select,
  SelectGroup,
  SelectGroupLabel,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  PROJECT_FILES_SIDEBAR_DEFAULT_WIDTH,
  PROJECT_FILES_SIDEBAR_KEYBOARD_STEP,
  PROJECT_FILES_SIDEBAR_MAX_WIDTH,
  PROJECT_FILES_SIDEBAR_MIN_WIDTH,
  PROJECT_FILES_SIDEBAR_STORAGE_KEY,
  PROJECT_FILES_SIDEBAR_WIDTH_STORAGE_KEY,
  projectFilesSidebarProjectKey,
  resolveProjectFilesSidebarProject,
} from "./projectFilesSidebarState";
import { THREAD_MAIN_CONTENT_MIN_WIDTH } from "./threadSidebarWidth";

const PROJECT_FILES_SIDEBAR_ID = "project-files-sidebar";

export function readProjectFilesSidebarOpen(): boolean {
  try {
    return getLocalStorageItem(PROJECT_FILES_SIDEBAR_STORAGE_KEY, Schema.Boolean) ?? false;
  } catch (error) {
    console.error("Could not read persisted project files sidebar state.", error);
    return false;
  }
}

export function persistProjectFilesSidebarOpen(open: boolean): void {
  try {
    setLocalStorageItem(PROJECT_FILES_SIDEBAR_STORAGE_KEY, open, Schema.Boolean);
  } catch (error) {
    console.error("Could not persist project files sidebar state.", error);
  }
}

export function ProjectFilesSidebar({
  viewportWidth,
  threadSidebarWidth,
}: {
  readonly viewportWidth: number;
  readonly threadSidebarWidth: number;
}) {
  const routeTarget = useParams({
    strict: false,
    select: (params) => resolveThreadRouteTarget(params),
  });
  const routeThreadRef = routeTarget?.kind === "server" ? routeTarget.threadRef : null;
  const activeThread = useThread(routeThreadRef);
  const activeDraft = useComposerDraftStore((store) =>
    routeTarget?.kind === "draft"
      ? store.getDraftSession(routeTarget.draftId)
      : routeTarget?.kind === "server"
        ? store.getDraftThreadByRef(routeTarget.threadRef)
        : null,
  );
  const activeProjectRef =
    activeThread !== null
      ? scopeProjectRef(activeThread.environmentId, activeThread.projectId)
      : activeDraft !== null
        ? scopeProjectRef(activeDraft.environmentId, activeDraft.projectId)
        : null;
  const activeProject = useProject(activeProjectRef);
  const projects = useProjects();
  const threads = useThreadShells();
  const projectSortOrder = useClientSettings((settings) => settings.sidebarProjectSortOrder);
  const orderedProjects = useMemo(
    () => sortScopedProjectsForSidebar(projects, threads, projectSortOrder),
    [projectSortOrder, projects, threads],
  );
  const activeProjectKey =
    activeProject === null ? null : projectFilesSidebarProjectKey(activeProject);
  const [selectedProjectKey, setSelectedProjectKey] = useState<string | null>(activeProjectKey);

  useEffect(() => {
    if (activeProjectKey !== null) {
      setSelectedProjectKey(activeProjectKey);
    }
  }, [activeProjectKey]);

  const selectedProject = resolveProjectFilesSidebarProject(
    orderedProjects,
    selectedProjectKey,
    activeProjectKey,
  );
  const projectItems = useMemo(
    () =>
      orderedProjects.map((project) => ({
        label: project.title,
        value: projectFilesSidebarProjectKey(project),
      })),
    [orderedProjects],
  );
  const threadSidebarOpen = useSidebarVisibility();
  const availableMaximumWidth =
    viewportWidth - (threadSidebarOpen ? threadSidebarWidth : 0) - THREAD_MAIN_CONTENT_MIN_WIDTH;
  const maximumWidth = Math.max(
    PROJECT_FILES_SIDEBAR_MIN_WIDTH,
    Math.min(PROJECT_FILES_SIDEBAR_MAX_WIDTH, availableMaximumWidth),
  );
  const { width, handlers, resizeTo } = useResizableWidth({
    storageKey: PROJECT_FILES_SIDEBAR_WIDTH_STORAGE_KEY,
    defaultWidth: PROJECT_FILES_SIDEBAR_DEFAULT_WIDTH,
    minWidth: PROJECT_FILES_SIDEBAR_MIN_WIDTH,
    maxWidth: maximumWidth,
    edge: "right",
  });

  if (selectedProject === null) {
    return null;
  }

  const handleResizeKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const nextWidth =
      event.key === "ArrowLeft"
        ? width - PROJECT_FILES_SIDEBAR_KEYBOARD_STEP
        : event.key === "ArrowRight"
          ? width + PROJECT_FILES_SIDEBAR_KEYBOARD_STEP
          : event.key === "Home"
            ? PROJECT_FILES_SIDEBAR_MIN_WIDTH
            : event.key === "End"
              ? maximumWidth
              : null;
    if (nextWidth === null) return;
    event.preventDefault();
    resizeTo(nextWidth);
  };

  return (
    <aside
      aria-label="Project files"
      className="relative flex h-dvh min-h-0 shrink-0 border-r border-border bg-background"
      data-project-files-sidebar=""
      id={PROJECT_FILES_SIDEBAR_ID}
      style={{ width: `${width}px` }}
    >
      <FileBrowserPanel
        environmentId={selectedProject.environmentId}
        cwd={selectedProject.workspaceRoot}
        projectName={selectedProject.title}
        projectSwitcher={
          <Select
            items={projectItems}
            modal={false}
            onValueChange={(value) => {
              if (value !== null) setSelectedProjectKey(value);
            }}
            value={projectFilesSidebarProjectKey(selectedProject)}
          >
            <SelectTrigger
              aria-label="Project files folder"
              className="-ml-1 h-7 min-h-7 w-full min-w-0 justify-start px-1 text-xs font-medium"
              size="xs"
              variant="ghost"
            >
              <FolderIcon aria-hidden className="size-3" />
              <SelectValue />
            </SelectTrigger>
            <SelectPopup>
              <SelectGroup>
                <SelectGroupLabel>Project files</SelectGroupLabel>
                {orderedProjects.map((project) => (
                  <SelectItem
                    key={projectFilesSidebarProjectKey(project)}
                    value={projectFilesSidebarProjectKey(project)}
                  >
                    <span className="min-w-0">
                      <span className="block truncate">{project.title}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {project.workspaceRoot}
                      </span>
                    </span>
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectPopup>
          </Select>
        }
      />
      <div
        aria-controls={PROJECT_FILES_SIDEBAR_ID}
        aria-label="Project files width"
        aria-orientation="vertical"
        aria-valuemax={maximumWidth}
        aria-valuemin={PROJECT_FILES_SIDEBAR_MIN_WIDTH}
        aria-valuenow={width}
        aria-valuetext={`${width} pixels`}
        className="group absolute inset-y-0 -right-1 z-20 w-2 cursor-col-resize select-none outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        onKeyDown={handleResizeKeyDown}
        role="separator"
        tabIndex={0}
        {...handlers}
      >
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-transparent transition-colors duration-150 group-hover:bg-border group-active:bg-primary/60"
        />
      </div>
    </aside>
  );
}
